import { useEffect, useState } from "react";
import adminApi from "@/lib/adminApi";
import { fmtNaira, fmtDate } from "@/lib/api";

const TYPES = [
  { value: "", label: "All" },
  { value: "sale", label: "Sales" },
  { value: "expense", label: "Expenses" },
  { value: "debt", label: "Debts" },
  { value: "payment_received", label: "Payments" },
  { value: "stock_added", label: "Stock" },
];

export default function AdminTransactions() {
  const [txs, setTxs] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminApi.get(`/admin/transactions${filter ? `?tx_type=${filter}` : ""}`)
      .then((r) => setTxs(r.data.transactions || []))
      .catch((err) => {
        // Iter42: never let a fetch failure blank the page. Show empty state
        // and log for the developer.
        // eslint-disable-next-line no-console
        console.warn("admin transactions fetch failed:", err);
        setTxs([]);
      })
      .finally(() => setLoading(false));
  }, [filter]);

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <div className="mb-6 flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Transactions</h1>
          <p className="text-sm text-kudi-ink/60">{txs.length} entries</p>
        </div>
        <div className="flex gap-1 bg-white border border-border rounded-full p-1 overflow-x-auto">
          {TYPES.map((t) => (
            <button key={t.value} onClick={() => setFilter(t.value)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition whitespace-nowrap ${filter === t.value ? "bg-kudi-green text-white" : "text-kudi-ink/60 hover:text-kudi-ink"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-accent/40 text-xs uppercase tracking-wider text-kudi-ink/60">
            <tr>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Description</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-right px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">When</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40">
            {loading ? (
              <tr><td colSpan={5} className="p-8 text-center text-kudi-ink/40">Loading...</td></tr>
            ) : txs.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-kudi-ink/40">No entries</td></tr>
            ) : txs.map((t) => (
              <tr key={t.id}>
                <td className="px-4 py-3"><TypeTag type={t.type} /></td>
                <td className="px-4 py-3 text-kudi-ink/80 max-w-md truncate">{t.description}</td>
                <td className="px-4 py-3 text-kudi-ink/70">{t.customer_name || "—"}</td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold">{fmtNaira(t.amount)}</td>
                <td className="px-4 py-3 text-xs text-kudi-ink/50">{fmtDate(t.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TypeTag({ type }) {
  const colors = {
    sale: "bg-emerald-50 text-emerald-700",
    expense: "bg-rose-50 text-rose-700",
    debt: "bg-amber-50 text-amber-700",
    payment_received: "bg-accent text-kudi-green",
    stock_added: "bg-blue-50 text-blue-700",
  };
  // Iter42: guard against null/undefined `type` so a malformed row never
  // crashes the render. Missing types display as `unknown`.
  const safeType = type || "unknown";
  const label = safeType.replace(/_/g, " ");
  return <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full ${colors[safeType] || "bg-muted"}`} data-testid={`type-tag-${safeType}`}>{label}</span>;
}
